import { useEffect, useMemo, useState } from 'react'
import type {
    InventoryDeductedItem,
    PackagingDeductedItem,
} from '@/lib/api/sales-order-api'
import type {
    RefundFieldErrors,
    RefundFormValue,
} from '@/lib/schemas/refund-schemas'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { isVirtualLot } from '@/lib/api/sales-order-api'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    ShelfField,
    sendsNewLot,
} from '@/components/sections/return-shelf-field'
import {
    useConfirmReturn,
    useOrderInventoryDeducted,
    useOrderPackagingDeducted,
    useSalesOrder,
} from '@/hooks/use-sales-order-query'
import { useAuth } from '@/hooks/use-auth'
import { RefundSection } from '@/components/sections/refund-section'
import {
    emptyRefund,
    refundFieldErrors,
    toRefundChoice,
} from '@/lib/schemas/refund-schemas'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    orderId: string
    onClose: () => void
    onSuccess?: () => void
}

interface PackagingRow {
    deducted: PackagingDeductedItem
    quantity: number
    enabled: boolean
}

interface InventoryRow {
    deducted: InventoryDeductedItem
    defective: boolean
    /** Chuỗi người dùng gõ; validate lúc bấm xác nhận. */
    defectQty: string
    /** Kệ nhập phần hàng tốt (M-38). Mặc định = kệ gợi ý của backend. */
    positionId: string | undefined
    /** Kệ nhập phần hàng lỗi (M-38). Mặc định = kệ hàng lỗi đã cấu hình. */
    defectPositionId: string | undefined
    /** Tick "Tạo lô mới" khi kệ hàng tốt trùng kệ cũ. Mặc định không tick. */
    positionNewLot: boolean
    /** Tick "Tạo lô mới" khi kệ hàng lỗi trùng kệ cũ. Mặc định tick. */
    defectNewLot: boolean
}

// Lô toàn hàng lỗi thì không còn phần tốt để chọn kệ — tất cả vào kệ lỗi.
function isAllDefect(r: InventoryRow): boolean {
    const qty = Number(r.defectQty)
    return r.defective && Number.isFinite(qty) && qty >= r.deducted.deducted_qty
}

// Dialog xác nhận hoàn đơn. Sản phẩm luôn hoàn đủ; dòng nào có hàng lỗi thì
// tick "Hàng lỗi" và nhập SL lỗi — phần lỗi vào kệ hàng lỗi, phần còn lại về lô
// cũ (FD-397). Mỗi lô chọn "Nhập hàng tốt về kệ" (mặc định kệ gợi ý) và, khi
// có hàng lỗi, "Nhập hàng lỗi về kệ" (mặc định kệ hàng lỗi, kệ nào cũng được).
// Kệ khác vị trí lô → lô mới cùng mã; đúng kệ cũ → theo ô "Tạo lô mới" (M-38).
// Vật tư bao bì chọn lô + SL nhập lại.
export function ConfirmReturnDialog({
    open,
    orderId,
    onClose,
    onSuccess,
}: Props) {
    const { data: pkgDeducted = [], isLoading: pkgLoading } =
        useOrderPackagingDeducted(orderId, open)
    const { data: invDeducted = [], isLoading: invLoading } =
        useOrderInventoryDeducted(orderId, open)
    const confirmReturn = useConfirmReturn()
    const workflow = useWorkflowTracking('order_return', {
        active: open,
        attemptKey: orderId,
        operation: 'confirm',
    })
    const [pkgRows, setPkgRows] = useState<PackagingRow[]>([])
    const [invRows, setInvRows] = useState<InventoryRow[]>([])
    const [defectErrors, setDefectErrors] = useState<Record<string, string>>({})
    const [defectPositionErrors, setDefectPositionErrors] = useState<
        Record<string, string>
    >({})
    // M-44: đơn đã thu tiền (vd chuyển khoản trước) → chọn hoàn hay giữ làm
    // tiền dư. Hoàn tối đa = tiền đã thu ròng của đơn.
    const { data: order } = useSalesOrder(orderId)
    const { hasPermission } = useAuth()
    const maxRefundable = order?.paid_amount ?? 0
    const [refund, setRefund] = useState<RefundFormValue>(emptyRefund)
    const [refundErrors, setRefundErrors] = useState<RefundFieldErrors>({})

    useEffect(() => {
        if (pkgDeducted.length > 0) {
            setPkgRows(
                pkgDeducted.map((d) => ({
                    deducted: d,
                    quantity: d.deducted_qty,
                    enabled: true,
                })),
            )
        }
    }, [pkgDeducted])

    useEffect(() => {
        // Bỏ lô ảo (FD-372): hàng đã giao nhưng kho chưa nhập bù, nên không có
        // hàng thật để hoàn về. Backend restock đã bỏ qua dòng này — bày ra cho
        // nhân viên gõ số là hứa một việc không xảy ra.
        const real = invDeducted.filter((d) => !isVirtualLot(d))
        if (real.length > 0) {
            setInvRows(
                real.map((d) => ({
                    deducted: d,
                    defective: false,
                    defectQty: String(d.deducted_qty),
                    positionId: d.suggested_position_id,
                    defectPositionId: d.suggested_defect_position_id,
                    positionNewLot: false,
                    defectNewLot: true,
                })),
            )
        }
    }, [invDeducted])

    const packagingRestoreItems = useMemo(
        () =>
            pkgRows
                .filter((r) => r.enabled && r.quantity > 0)
                .map((r) => ({
                    packaging_lot_id: r.deducted.packaging_lot_id,
                    quantity: r.quantity,
                })),
        [pkgRows],
    )

    const totalRestorePkg = packagingRestoreItems.reduce(
        (sum, it) => sum + it.quantity,
        0,
    )

    const handlePkgQtyChange = (lotId: string, value: string) => {
        const num = parseFloat(value)
        const safe = Number.isFinite(num) && num >= 0 ? num : 0
        setPkgRows((prev) =>
            prev.map((r) => {
                if (r.deducted.packaging_lot_id !== lotId) return r
                const max = r.deducted.deducted_qty
                return { ...r, quantity: Math.min(safe, max) }
            }),
        )
    }

    const togglePkgRow = (lotId: string) => {
        setPkgRows((prev) =>
            prev.map((r) =>
                r.deducted.packaging_lot_id === lotId
                    ? { ...r, enabled: !r.enabled }
                    : r,
            ),
        )
    }

    const toggleDefect = (lotId: string) => {
        setInvRows((prev) =>
            prev.map((r) =>
                r.deducted.inventory_lot_id === lotId
                    ? {
                          ...r,
                          defective: !r.defective,
                          defectQty: String(r.deducted.deducted_qty),
                          defectPositionId:
                              r.deducted.suggested_defect_position_id,
                          defectNewLot: true,
                      }
                    : r,
            ),
        )
        setDefectErrors((prev) => {
            const next = { ...prev }
            delete next[lotId]
            return next
        })
        setDefectPositionErrors((prev) => {
            const next = { ...prev }
            delete next[lotId]
            return next
        })
    }

    const handleDefectQtyChange = (lotId: string, value: string) => {
        setInvRows((prev) =>
            prev.map((r) =>
                r.deducted.inventory_lot_id === lotId
                    ? { ...r, defectQty: value }
                    : r,
            ),
        )
        // Sửa lại số là lỗi cũ hết hiệu lực: giữ nó lại thì người dùng thấy câu
        // báo lỗi bên cạnh một giá trị đã đúng.
        setDefectErrors((prev) => {
            if (!(lotId in prev)) return prev
            const next = { ...prev }
            delete next[lotId]
            return next
        })
    }

    const handlePositionChange = (lotId: string, positionId: string) => {
        setInvRows((prev) =>
            prev.map((r) =>
                r.deducted.inventory_lot_id === lotId
                    ? { ...r, positionId }
                    : r,
            ),
        )
    }

    const setRowNewLot = (
        lotId: string,
        field: 'positionNewLot' | 'defectNewLot',
        checked: boolean,
    ) => {
        setInvRows((prev) =>
            prev.map((r) =>
                r.deducted.inventory_lot_id === lotId
                    ? { ...r, [field]: checked }
                    : r,
            ),
        )
    }

    const handleDefectPositionChange = (lotId: string, positionId: string) => {
        setInvRows((prev) =>
            prev.map((r) =>
                r.deducted.inventory_lot_id === lotId
                    ? { ...r, defectPositionId: positionId }
                    : r,
            ),
        )
        setDefectPositionErrors((prev) => {
            if (!(lotId in prev)) return prev
            const next = { ...prev }
            delete next[lotId]
            return next
        })
    }

    // Một dòng/lô; lô toàn hàng lỗi không gửi kệ hàng tốt.
    const buildRestockPositions = () => {
        const byLot = new Map<
            string,
            { product_lot_id: string; position_id: string; new_lot: boolean }
        >()
        for (const r of invRows) {
            if (!r.positionId || isAllDefect(r)) continue
            byLot.set(r.deducted.inventory_lot_id, {
                product_lot_id: r.deducted.inventory_lot_id,
                position_id: r.positionId,
                new_lot: sendsNewLot(
                    r.positionId,
                    r.deducted.current_position_id,
                    r.positionNewLot,
                ),
            })
        }
        return [...byLot.values()]
    }

    // Validate lúc bấm xác nhận (không khoá nút): lỗi theo lô + payload.
    const buildDefectItems = () => {
        const errors: Record<string, string> = {}
        const positionErrors: Record<string, string> = {}
        const items: { product_lot_id: string; quantity: number }[] = []
        const positions: {
            product_lot_id: string
            position_id: string
            new_lot: boolean
        }[] = []
        for (const r of invRows) {
            if (!r.defective) continue
            const lotId = r.deducted.inventory_lot_id
            if (r.defectPositionId) {
                positions.push({
                    product_lot_id: lotId,
                    position_id: r.defectPositionId,
                    new_lot: sendsNewLot(
                        r.defectPositionId,
                        r.deducted.current_position_id,
                        r.defectNewLot,
                    ),
                })
            } else {
                positionErrors[lotId] =
                    m.confirm_return_defect_position_required()
            }
            const qty = Number(r.defectQty)
            if (
                r.defectQty.trim() === '' ||
                !Number.isFinite(qty) ||
                qty <= 0
            ) {
                errors[lotId] = m.confirm_return_defect_qty_min()
            } else if (qty > r.deducted.deducted_qty) {
                errors[lotId] = m.confirm_return_defect_qty_max({
                    max: String(r.deducted.deducted_qty),
                })
            } else {
                items.push({ product_lot_id: lotId, quantity: qty })
            }
        }
        return { errors, positionErrors, items, positions }
    }

    const handleConfirm = async () => {
        const { errors, positionErrors, items, positions } = buildDefectItems()
        setDefectErrors(errors)
        setDefectPositionErrors(positionErrors)
        const refundErrs =
            maxRefundable > 0 ? refundFieldErrors(refund, maxRefundable) : {}
        setRefundErrors(refundErrs)
        if (
            Object.keys(errors).length > 0 ||
            Object.keys(positionErrors).length > 0 ||
            Object.keys(refundErrs).length > 0
        )
            return
        await workflow.run(
            () =>
                confirmReturn.mutateAsync({
                    id: orderId,
                    data: {
                        packaging_restore_items: packagingRestoreItems,
                        defect_items: items,
                        restock_positions: buildRestockPositions(),
                        defect_positions: positions,
                        refund:
                            maxRefundable > 0
                                ? toRefundChoice(refund)
                                : undefined,
                    },
                }),
            (result) => result.data?.id,
        )
        onSuccess?.()
        onClose()
    }

    const isLoading = pkgLoading || invLoading

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                onChangeCapture={() => workflow.interact('items')}
                className="max-w-3xl max-h-[85vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle>{m.confirm_return_title()}</DialogTitle>
                    <DialogDescription>
                        {m.confirm_return_desc()}
                    </DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <div className="text-sm text-muted-foreground">
                        {m.confirm_return_loading()}
                    </div>
                ) : (
                    <div className="space-y-6">
                        <section className="space-y-2">
                            <h3 className="text-sm font-semibold">
                                {m.confirm_return_section_products()}
                            </h3>
                            {invRows.length === 0 ? (
                                <div className="text-sm text-muted-foreground">
                                    {m.confirm_return_inv_empty()}
                                </div>
                            ) : (
                                <>
                                    <p className="text-xs text-muted-foreground">
                                        {m.confirm_return_defect_hint()}
                                    </p>
                                    <div className="grid grid-cols-[1fr_72px_72px_104px] gap-3 text-xs font-medium text-muted-foreground border-b pb-2">
                                        <div>
                                            {m.confirm_return_col_product_lot()}
                                        </div>
                                        <div className="text-right">
                                            {m.confirm_return_col_deducted_short()}
                                        </div>
                                        <div className="text-center">
                                            {m.confirm_return_col_defect()}
                                        </div>
                                        <div>
                                            {m.confirm_return_defect_qty_label()}
                                        </div>
                                    </div>
                                    {invRows.map((r) => {
                                        const lotId =
                                            r.deducted.inventory_lot_id
                                        const errorId = `defect-qty-error-${lotId}`
                                        const error = defectErrors[lotId]
                                        const pickerId = `return-position-${lotId}`
                                        return (
                                            <div
                                                key={lotId}
                                                className="grid grid-cols-[1fr_72px_72px_104px] gap-3 items-start text-sm"
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-medium break-words">
                                                        {
                                                            r.deducted
                                                                .product_name
                                                        }
                                                        {r.deducted
                                                            .product_sku ? (
                                                            <span className="text-xs text-muted-foreground font-mono ml-2">
                                                                {
                                                                    r.deducted
                                                                        .product_sku
                                                                }
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {m.confirm_return_lot_prefix()}{' '}
                                                        <span className="font-mono">
                                                            {
                                                                r.deducted
                                                                    .lot_code
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right text-muted-foreground pt-1">
                                                    {r.deducted.deducted_qty}
                                                </div>
                                                <label className="flex h-8 items-center justify-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 cursor-pointer"
                                                        checked={r.defective}
                                                        aria-label={m.confirm_return_col_defect()}
                                                        onChange={() =>
                                                            toggleDefect(lotId)
                                                        }
                                                    />
                                                </label>
                                                <div>
                                                    {r.defective && (
                                                        <>
                                                            <Input
                                                                type="number"
                                                                min={0}
                                                                max={
                                                                    r.deducted
                                                                        .deducted_qty
                                                                }
                                                                aria-label={m.confirm_return_defect_qty_label()}
                                                                aria-invalid={Boolean(
                                                                    error,
                                                                )}
                                                                aria-describedby={
                                                                    error
                                                                        ? errorId
                                                                        : undefined
                                                                }
                                                                value={
                                                                    r.defectQty
                                                                }
                                                                onChange={(e) =>
                                                                    handleDefectQtyChange(
                                                                        lotId,
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                className="h-8"
                                                            />
                                                            {error && (
                                                                <p
                                                                    id={errorId}
                                                                    className="mt-1 text-xs text-destructive"
                                                                >
                                                                    {error}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                <div className="col-span-full space-y-2 pb-2 border-b">
                                                    {!isAllDefect(r) && (
                                                        <ShelfField
                                                            id={pickerId}
                                                            label={m.confirm_return_position_label()}
                                                            value={r.positionId}
                                                            onChange={(id) =>
                                                                handlePositionChange(
                                                                    lotId,
                                                                    id,
                                                                )
                                                            }
                                                            placeholder={m.confirm_return_position_placeholder()}
                                                            fallbackLabel={
                                                                r.deducted
                                                                    .suggested_position_label
                                                            }
                                                            currentPositionId={
                                                                r.deducted
                                                                    .current_position_id
                                                            }
                                                            newLot={
                                                                r.positionNewLot
                                                            }
                                                            newLotLabel={m.confirm_return_new_lot()}
                                                            onNewLotChange={(
                                                                checked,
                                                            ) =>
                                                                setRowNewLot(
                                                                    lotId,
                                                                    'positionNewLot',
                                                                    checked,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {r.defective && (
                                                        <ShelfField
                                                            id={`${pickerId}-defect`}
                                                            label={m.confirm_return_defect_position_label()}
                                                            value={
                                                                r.defectPositionId
                                                            }
                                                            onChange={(id) =>
                                                                handleDefectPositionChange(
                                                                    lotId,
                                                                    id,
                                                                )
                                                            }
                                                            placeholder={m.confirm_return_defect_position_placeholder()}
                                                            fallbackLabel={
                                                                r.deducted
                                                                    .suggested_defect_position_label
                                                            }
                                                            error={
                                                                defectPositionErrors[
                                                                    lotId
                                                                ]
                                                            }
                                                            currentPositionId={
                                                                r.deducted
                                                                    .current_position_id
                                                            }
                                                            newLot={
                                                                r.defectNewLot
                                                            }
                                                            newLotLabel={m.confirm_return_defect_new_lot()}
                                                            onNewLotChange={(
                                                                checked,
                                                            ) =>
                                                                setRowNewLot(
                                                                    lotId,
                                                                    'defectNewLot',
                                                                    checked,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </>
                            )}
                        </section>

                        <section className="space-y-2">
                            <h3 className="text-sm font-semibold">
                                {m.confirm_return_section_packaging()}
                            </h3>
                            {pkgRows.length === 0 ? (
                                <div className="text-sm text-muted-foreground">
                                    {m.confirm_return_empty()}
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-[24px_1fr_140px_120px] gap-3 text-xs font-medium text-muted-foreground border-b pb-2">
                                        <div></div>
                                        <div>
                                            {m.confirm_return_col_material()}
                                        </div>
                                        <div className="text-right">
                                            {m.confirm_return_col_deducted()}
                                        </div>
                                        <div className="text-right">
                                            {m.confirm_return_col_restore()}
                                        </div>
                                    </div>
                                    {pkgRows.map((r) => (
                                        <div
                                            key={r.deducted.packaging_lot_id}
                                            className="grid grid-cols-[24px_1fr_140px_120px] gap-3 items-center text-sm"
                                        >
                                            <Input
                                                type="checkbox"
                                                className="h-4 w-4"
                                                checked={r.enabled}
                                                onChange={() =>
                                                    togglePkgRow(
                                                        r.deducted
                                                            .packaging_lot_id,
                                                    )
                                                }
                                            />
                                            <div>
                                                <div className="font-medium">
                                                    {r.deducted.material_name}
                                                    <span className="text-xs text-muted-foreground font-mono ml-2">
                                                        {
                                                            r.deducted
                                                                .material_sku
                                                        }
                                                    </span>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {m.confirm_return_lot_label()}{' '}
                                                    <span className="font-mono">
                                                        {r.deducted.lot_code}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right text-muted-foreground">
                                                {r.deducted.deducted_qty}{' '}
                                                {r.deducted.material_unit}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    max={
                                                        r.deducted.deducted_qty
                                                    }
                                                    value={
                                                        r.enabled
                                                            ? r.quantity
                                                            : 0
                                                    }
                                                    disabled={!r.enabled}
                                                    onChange={(e) =>
                                                        handlePkgQtyChange(
                                                            r.deducted
                                                                .packaging_lot_id,
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="h-8"
                                                />
                                                <Label className="text-xs text-muted-foreground">
                                                    {r.deducted.material_unit}
                                                </Label>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="text-right text-sm pt-2 border-t">
                                        <span className="text-muted-foreground">
                                            {m.confirm_return_total_label()}{' '}
                                        </span>
                                        <span className="font-semibold">
                                            {totalRestorePkg}{' '}
                                            {m.confirm_return_unit()}
                                        </span>
                                        {totalRestorePkg === 0 && (
                                            <span className="text-xs text-muted-foreground ml-2">
                                                {m.confirm_return_no_receipt_note()}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                        </section>

                        <RefundSection
                            value={refund}
                            onChange={(v) => {
                                setRefund(v)
                                setRefundErrors({})
                            }}
                            maxRefundable={maxRefundable}
                            canRefund={hasPermission('customers.refund')}
                            errors={refundErrors}
                        />
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={confirmReturn.isPending}
                    >
                        {m.confirm_return_cancel_btn()}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading || confirmReturn.isPending}
                    >
                        {confirmReturn.isPending
                            ? m.confirm_return_processing()
                            : m.confirm_return_btn()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
